import { useEffect, useState } from "react";
import { Search, UserPlus } from "lucide-react";
import { errorMessage } from "@/lib/api";
import type {
  AthleteDirectoryEntry,
  Player,
  RosterAssignmentCreate,
} from "@/lib/types";
import { useToast } from "@/components/ui/toast";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { TkticDateInput } from "@/components/ui/TkticDateInput";
import { TkticSelect } from "@/components/ui/TkticSelect";
import { useAthleteDirectory } from "@/features/athletes/hooks/useAthletes";
import { assignmentChipLabel } from "@/features/athletes/athleteDisplay";
import { useScopeStore } from "@/stores/scope";
import { CoachModal } from "./CoachModal";
import type { RosterMutations } from "../hooks/useRoster";
import { PLAYER_POSITION_OPTIONS } from "../playerPositions";

/**
 * Add player — Coach > Roster (#908 D14).
 *
 * "Add player" is a search over the CLUB athlete directory first, so a
 * child already rostered on another team (or a past season's team) is
 * rostered again instead of duplicated. Picking a match fills the
 * assignment fields (jersey, position, start date) and creates one
 * assignment of that athlete on the active team. The "Create new athlete"
 * fallback is always available — emphasised when nothing matches — and
 * creates the athlete inline (display name, optional given / family name
 * and birth year) with the same assignment in one request.
 *
 * Directory disclosure is name, status and team/season assignments only
 * (§5.3); `birth_year` arrives only when the viewer is the athlete's own
 * team staff or a club admin, and is shown only then. An athlete with an
 * ACTIVE assignment on this team is listed but disabled (the backend keeps
 * one assignment per athlete per team, D8); one whose assignment here has
 * ENDED stays selectable as a rejoin — the backend reinstates that row.
 */

const SEARCH_DEBOUNCE_MS = 250;

export type AddPlayerModalProps = {
  mutations: RosterMutations;
  onCreated: (player: Player) => void;
  onClose: () => void;
};

type Mode = "search" | "existing" | "create";

export function AddPlayerModal({
  mutations,
  onCreated,
  onClose,
}: AddPlayerModalProps) {
  const toast = useToast();
  const teamId = useScopeStore((s) => s.teamId);
  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");
  const [mode, setMode] = useState<Mode>("search");
  const [selected, setSelected] = useState<AthleteDirectoryEntry | null>(null);
  // New-athlete fields (create mode).
  const [displayName, setDisplayName] = useState("");
  const [givenName, setGivenName] = useState("");
  const [familyName, setFamilyName] = useState("");
  const [birthYear, setBirthYear] = useState("");
  // Assignment fields (both modes).
  const [jersey, setJersey] = useState("");
  const [position, setPosition] = useState("");
  const [startsOn, setStartsOn] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const handle = window.setTimeout(
      () => setDebounced(query.trim()),
      SEARCH_DEBOUNCE_MS,
    );
    return () => window.clearTimeout(handle);
  }, [query]);

  const directory = useAthleteDirectory(debounced, {
    status: "active",
    enabled: mode === "search" && debounced.length > 0,
  });
  const matches = debounced.length > 0 ? (directory.data ?? []) : [];
  const searching =
    debounced.length > 0 && (directory.isFetching || query.trim() !== debounced);
  const noMatches = debounced.length > 0 && !searching && matches.length === 0;

  const pickExisting = (athlete: AthleteDirectoryEntry) => {
    setSelected(athlete);
    setError(null);
    setMode("existing");
  };

  const startCreate = () => {
    setSelected(null);
    setDisplayName(query.trim());
    setError(null);
    setMode("create");
  };

  const backToSearch = () => {
    setSelected(null);
    setError(null);
    setMode("search");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (mode === "search") return;
    if (!teamId) {
      setError("Pick a team before adding players.");
      return;
    }
    const assignment = {
      team_id: teamId,
      jersey_number: jersey.trim(),
      position,
      starts_on: startsOn || null,
    };
    let body: RosterAssignmentCreate;
    if (mode === "existing") {
      if (!selected) return;
      body = { ...assignment, athlete_id: selected.id };
    } else {
      const name = displayName.trim();
      if (!name) {
        setError("Player name is required.");
        return;
      }
      const year = birthYear.trim();
      if (year && !/^\d{4}$/.test(year)) {
        setError("Birth year must be four digits.");
        return;
      }
      body = {
        ...assignment,
        athlete: {
          display_name: name,
          given_name: givenName.trim() || null,
          family_name: familyName.trim() || null,
          birth_year: year ? Number(year) : null,
        },
      };
    }
    setError(null);
    setSubmitting(true);
    try {
      const player = await mutations.createAssignment(body);
      toast.success("Player added");
      onCreated(player);
    } catch (err) {
      setError(errorMessage(err));
      toast.error("Couldn't add player", { description: errorMessage(err) });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <CoachModal
      title="Add player"
      description="Search the club directory first so a player already on another team or season is rostered, not duplicated."
      onClose={onClose}
      data-testid="roster-add-player-modal"
      footer={
        <div className="flex flex-wrap justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          {mode !== "search" ? (
            <Button
              type="submit"
              form="roster-add-player-form"
              disabled={submitting}
              data-testid="roster-quick-add-submit"
            >
              {submitting
                ? "Adding…"
                : mode === "existing"
                  ? "Add to team"
                  : "Create and add"}
            </Button>
          ) : null}
        </div>
      }
    >
      <form
        id="roster-add-player-form"
        onSubmit={handleSubmit}
        className="flex flex-col gap-4"
      >
        {mode === "search" ? (
          <>
            <Field label="Find in the club directory">
              <span className="relative block">
                <Search
                  aria-hidden="true"
                  className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-ink-muted"
                />
                <input
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Athlete name"
                  autoFocus
                  data-testid="roster-add-directory-search"
                  className="min-h-12 w-full rounded border border-rule bg-field pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-accent"
                />
              </span>
            </Field>

            {debounced.length > 0 ? (
              <div
                className="flex flex-col gap-2"
                data-testid="roster-add-directory-results"
                data-state={searching ? "loading" : "ready"}
              >
                {searching && matches.length === 0 ? (
                  <p className="text-xs text-ink-muted font-mono">
                    Searching…
                  </p>
                ) : null}
                {directory.isError ? (
                  <p className="text-xs text-destructive font-mono">
                    {errorMessage(directory.error)}
                  </p>
                ) : null}
                {matches.length > 0 ? (
                  <ul className="divide-y divide-rule rounded border border-rule bg-elevated">
                    {matches.map((athlete) => (
                      <DirectoryMatch
                        key={athlete.id}
                        athlete={athlete}
                        teamId={teamId}
                        onPick={() => pickExisting(athlete)}
                      />
                    ))}
                  </ul>
                ) : noMatches ? (
                  <p
                    className="text-sm text-ink-muted"
                    data-testid="roster-add-directory-empty"
                  >
                    No athlete named “{debounced}” in the club directory.
                  </p>
                ) : null}
              </div>
            ) : (
              <p className="text-sm text-ink-muted">
                Type a name to search, or create a new athlete.
              </p>
            )}

            <div className="flex flex-wrap items-center gap-3 border-t border-rule pt-3">
              <Button
                type="button"
                variant={noMatches ? "primary" : "outline"}
                onClick={startCreate}
                data-testid="roster-add-create-new"
              >
                <UserPlus aria-hidden="true" className="size-4" />
                Create new athlete
              </Button>
              <span className="text-xs text-ink-muted">
                {noMatches
                  ? "Nothing matched — add them as a new athlete."
                  : "Only when the player is not in the directory yet."}
              </span>
            </div>
          </>
        ) : (
          <>
            {mode === "existing" && selected ? (
              <div
                className="flex flex-wrap items-center justify-between gap-3 rounded border border-rule bg-elevated px-3 py-2.5"
                data-testid="roster-add-selected-athlete"
                data-athlete-id={selected.id}
              >
                <div className="min-w-0">
                  <p className="truncate font-display text-sm text-ink">
                    {selected.display_name}
                    {selected.birth_year ? (
                      <span className="ml-2 text-xs text-ink-muted">
                        born {selected.birth_year}
                      </span>
                    ) : null}
                  </p>
                  <AssignmentChips athlete={selected} teamId={teamId} />
                </div>
                <Button type="button" variant="ghost" size="sm" onClick={backToSearch}>
                  Change
                </Button>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between gap-3">
                  <p className="text-sm text-ink-muted">New athlete</p>
                  <Button type="button" variant="ghost" size="sm" onClick={backToSearch}>
                    Back to search
                  </Button>
                </div>
                <Field label="Display name *">
                  <input
                    type="text"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    placeholder="Name as it appears on the roster"
                    data-testid="roster-quick-add-name"
                    autoFocus
                    className="min-h-12 w-full rounded border border-rule bg-field px-3 text-sm focus:outline-none focus:ring-2 focus:ring-accent"
                  />
                </Field>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                  <Field label="Given name" helper="optional">
                    <input
                      type="text"
                      value={givenName}
                      onChange={(e) => setGivenName(e.target.value)}
                      data-testid="roster-quick-add-given-name"
                      className="min-h-12 w-full rounded border border-rule bg-field px-3 text-sm focus:outline-none focus:ring-2 focus:ring-accent"
                    />
                  </Field>
                  <Field label="Family name" helper="optional">
                    <input
                      type="text"
                      value={familyName}
                      onChange={(e) => setFamilyName(e.target.value)}
                      data-testid="roster-quick-add-family-name"
                      className="min-h-12 w-full rounded border border-rule bg-field px-3 text-sm focus:outline-none focus:ring-2 focus:ring-accent"
                    />
                  </Field>
                  <Field label="Birth year" helper="optional">
                    <input
                      type="text"
                      inputMode="numeric"
                      value={birthYear}
                      onChange={(e) => setBirthYear(e.target.value)}
                      placeholder="YYYY"
                      data-testid="roster-quick-add-birth-year"
                      className="min-h-12 w-full rounded border border-rule bg-field px-3 text-sm focus:outline-none focus:ring-2 focus:ring-accent"
                    />
                  </Field>
                </div>
              </>
            )}

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <Field label="Jersey number">
                <input
                  type="text"
                  value={jersey}
                  onChange={(e) => setJersey(e.target.value)}
                  placeholder="Optional"
                  data-testid="roster-quick-add-jersey"
                  className="min-h-12 w-full rounded border border-rule bg-field px-3 text-sm focus:outline-none focus:ring-2 focus:ring-accent"
                />
              </Field>
              <Field label="Position">
                <TkticSelect
                  value={position}
                  onChange={setPosition}
                  data-testid="roster-quick-add-position"
                  ariaLabel="Position"
                  options={PLAYER_POSITION_OPTIONS.map((o) => ({
                    value: o.value,
                    label: o.label || "Optional",
                  }))}
                />
              </Field>
              <Field label="Starts on" helper="optional">
                <TkticDateInput
                  value={startsOn}
                  onChange={setStartsOn}
                  data-testid="roster-quick-add-starts-on"
                  buttonAriaLabel="Pick a start date"
                />
              </Field>
            </div>
          </>
        )}

        {error ? (
          <p
            className="text-destructive font-mono text-xs"
            data-testid="roster-quick-add-error"
          >
            {error}
          </p>
        ) : null}
      </form>
    </CoachModal>
  );
}

function DirectoryMatch({
  athlete,
  teamId,
  onPick,
}: {
  athlete: AthleteDirectoryEntry;
  teamId: string | null;
  onPick: () => void;
}) {
  const here = (athlete.assignments ?? []).find((a) => a.team_id === teamId);
  const rejoin = here?.assignment_status === "ended";
  const disabled = !!here && !rejoin;
  return (
    <li>
      <button
        type="button"
        onClick={onPick}
        disabled={disabled}
        data-testid="roster-add-directory-match"
        data-athlete-id={athlete.id}
        data-disabled={disabled ? "true" : undefined}
        className="flex w-full flex-wrap items-center justify-between gap-2 px-3 py-2.5 text-left transition-colors hover:bg-field disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:bg-transparent"
      >
        <span className="min-w-0 flex-1">
          <span className="block truncate font-display text-sm text-ink">
            {athlete.display_name}
            {athlete.birth_year ? (
              <span className="ml-2 text-xs text-ink-muted">
                born {athlete.birth_year}
              </span>
            ) : null}
          </span>
          <AssignmentChips athlete={athlete} teamId={teamId} />
        </span>
        {rejoin ? (
          <Badge variant="accent" data-testid="roster-add-directory-rejoin">
            Rejoin this team
          </Badge>
        ) : here ? (
          <Badge variant="muted" data-testid="roster-add-directory-already">
            Already on this team
          </Badge>
        ) : null}
      </button>
    </li>
  );
}

/** "Team · Season" chip per assignment of the athlete (§5.3 disclosure). */
function AssignmentChips({
  athlete,
  teamId,
}: {
  athlete: AthleteDirectoryEntry;
  teamId: string | null;
}) {
  const assignments = athlete.assignments ?? [];
  if (assignments.length === 0) {
    return (
      <span className="mt-1 block text-xs text-ink-muted">
        Not on a team yet
      </span>
    );
  }
  return (
    <span className="mt-1 flex flex-wrap gap-1">
      {assignments.map((a) => (
        <span
          key={a.player_id}
          data-testid="roster-add-directory-chip"
          data-assignment-status={a.assignment_status}
          className={[
            "inline-flex items-center rounded-full border px-2 py-0.5 text-xs",
            a.team_id === teamId
              ? "border-accent/40 bg-accent/10 text-ink"
              : "border-rule bg-canvas text-ink-muted",
            a.assignment_status === "ended" ? "line-through opacity-70" : "",
          ].join(" ")}
        >
          {assignmentChipLabel(a)}
        </span>
      ))}
    </span>
  );
}

function Field(props: {
  label: string;
  helper?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs font-display uppercase tracking-wider text-ink-muted">
        {props.label}
        {props.helper ? (
          <span className="ml-1 normal-case tracking-normal text-ink-muted/70">
            ({props.helper})
          </span>
        ) : null}
      </span>
      {props.children}
    </label>
  );
}
